import React, { useState } from 'react';
import { Mail, Phone, MapPin, Send } from 'lucide-react';
import { companyInfo } from '../data';

const Contact = ({ serviceName }) => {
  const [status, setStatus] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus('sending');
    
    const formData = new FormData(e.target);
    const data = Object.fromEntries(formData.entries());

    try {
      const response = await fetch('https://api.dev.auxo.dk/webform/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...data,
          subject: serviceName ? `Forespørgsel vedr. ${serviceName}` : 'Kontakt fra hjemmesiden'
        }),
      });

      if (response.ok) {
        setStatus('success');
        e.target.reset();
      } else {
        setStatus('error');
      }
    } catch (error) {
      setStatus('error');
    }
  };

  return (
    <section id="contact" className="py-24 bg-slate-900 text-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
          <div>
            <h2 className="text-orange-500 font-bold tracking-wider uppercase text-sm mb-3">Kontakt</h2>
            <h3 className="text-4xl font-bold mb-6">
              {serviceName ? (
                <>Få et tilbud på <br /><span className="text-orange-500">{serviceName}</span></>
              ) : (
                <>Lad os tale om dit <br /><span className="text-orange-500">næste brolægningsprojekt</span></>
              )}
            </h3>
            <p className="text-slate-400 text-lg mb-10 max-w-md">
              {serviceName 
                ? `Har du brug for hjælp til ${serviceName.toLowerCase()}? Jeg står klar med professionel rådgivning og et uforpligtende tilbud.`
                : "Har du spørgsmål eller ønsker du et uforpligtende tilbud? Jeg står klar til at hjælpe dig med professionel rådgivning."
              }
            </p>

            <div className="space-y-8">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-white/10 rounded-lg flex items-center justify-center shrink-0">
                  <Phone className="w-6 h-6 text-orange-500" />
                </div>
                <div>
                  <p className="text-sm text-slate-400 font-medium">Ring til mig</p>
                  <a href={`tel:+45${companyInfo.phone.replace(/\s/g, '')}`} className="text-xl font-bold hover:text-orange-500 transition-colors">
                    +45 {companyInfo.phone}
                  </a>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-white/10 rounded-lg flex items-center justify-center shrink-0">
                  <Mail className="w-6 h-6 text-orange-500" />
                </div>
                <div>
                  <p className="text-sm text-slate-400 font-medium">Send en mail</p>
                  <a href={`mailto:${companyInfo.email}`} className="text-xl font-bold hover:text-orange-500 transition-colors break-all">
                    {companyInfo.email}
                  </a>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-white/10 rounded-lg flex items-center justify-center shrink-0">
                  <MapPin className="w-6 h-6 text-orange-500" />
                </div>
                <div>
                  <p className="text-sm text-slate-400 font-medium">Besøg efter aftale</p>
                  <p className="text-xl font-bold">{companyInfo.fullAddress}</p>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-2xl p-8 lg:p-10 shadow-2xl">
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Honeypot */}
              <div className="sr-only absolute" aria-hidden="true">
                <label htmlFor="website_url">Website URL</label>
                <input id="website_url" type="text" name="website_url" tabIndex="-1" />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-slate-700 mb-2 text-left">Navn</label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    required
                    className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 text-slate-900 transition-all"
                    placeholder="Dit navn"
                  />
                </div>
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-slate-700 mb-2 text-left">Email</label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    required
                    className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 text-slate-900 transition-all"
                    placeholder="din@email.dk"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="phone" className="block text-sm font-medium text-slate-700 mb-2 text-left">Telefon</label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 text-slate-900 transition-all"
                  placeholder="+45 00 00 00 00"
                />
              </div>

              <div>
                <label htmlFor="message" className="block text-sm font-medium text-slate-700 mb-2 text-left">Besked</label>
                <textarea
                  id="message"
                  name="message"
                  rows="4"
                  required
                  defaultValue={serviceName ? `Hej, jeg er interesseret i at høre mere om ${serviceName.toLowerCase()}...` : ''}
                  className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 text-slate-900 transition-all resize-none"
                  placeholder="Fortæl om dit projekt..."
                ></textarea>
              </div>

              <button
                type="submit"
                disabled={status === 'sending'}
                className={`w-full py-4 rounded-lg font-bold text-lg flex items-center justify-center gap-2 transition-all ${
                  status === 'success' 
                  ? 'bg-green-500 text-white' 
                  : 'bg-orange-600 text-white hover:bg-orange-700'
                }`}
              >
                {status === 'sending' ? 'Sender...' : status === 'success' ? 'Besked sendt!' : (
                  <>
                    Send besked
                    <Send className="w-5 h-5" />
                  </>
                )}
              </button>
              
              {status === 'error' && (
                <p className="text-red-500 text-sm mt-2">Der skete en fejl. Prøv venligst igen senere.</p>
              )}
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
